import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'imageWrapper',
  'image',
  'swiperImage',
  'badge',
  'hoverWrapper',
  'primaryBlock',
  'lifestyleBlock',
  'lifestyleImage',
  'swatchSection',
  'swatchRow',
  'swatchItem',
  'swatchMore',
  'content',
  'description',
  'categoryLink',
  'infoStack',
  'ctaButton',
  'skeletonImage',
  'skeletonText'
]);

const SWATCH_SIZE = '12px';
const SWATCH_RADIUS = '3px';
const SWATCH_ROW_GAP = '5px';
/** Extra offset after flex row gap so "+ more" sits 8px from last swatch (Figma 3138-10828). */
const SWATCH_MORE_OFFSET = '3px';

/** Range tile info — title 3 lines, short description 2 lines (matches PLP tiles). */
const CARD_TITLE_LINE_CLAMP = {
  display: '-webkit-box',
  noOfLines: 3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  whiteSpace: 'normal',
  width: '100%',
};

const CARD_DESC_LINE_CLAMP = {
  display: '-webkit-box',
  noOfLines: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  whiteSpace: 'normal',
  width: '100%',
};

/** Teaser tile uses description as the card title — no line clamp. */
const CARD_DESC_LINE_CLAMP_RESET = {
  display: 'block',
  overflow: 'visible',
  textOverflow: 'unset',
  WebkitBoxOrient: 'unset',
  WebkitLineClamp: 'unset',
  whiteSpace: 'normal',
};

/** Apply line-clamp to category link text + description inside card content. */
const CARD_CONTENT_LINE_CLAMP_SX = {
  '& a .chakra-text': CARD_TITLE_LINE_CLAMP,
  '& .contentful-card__description:not(.contentful-card__description--tile)': CARD_DESC_LINE_CLAMP,
  '& .contentful-card__description:not(.contentful-card__description--tile) p': CARD_DESC_LINE_CLAMP,
  '& .contentful-card__description--tile': CARD_DESC_LINE_CLAMP_RESET,
  '& .contentful-card__description--tile p': CARD_DESC_LINE_CLAMP_RESET,
};

/** Shared badge chrome — Figma card tags (3138-3977 desktop, 3138-4928 mobile). */
const CARD_BADGE_BASE = {
  alignItems: 'center',
  borderRadius: '4px',
  display: 'inline-flex',
  fontFamily: 'body',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  letterSpacing: 0,
  lineHeight: 1.2,
  paddingX: 2,
  paddingY: 1,
  position: 'absolute',
  top: {base: 2, lg: 5},
  left: {base: 2, lg: 5},
  zIndex: 1,
};

/**
 * Card contentful model styles.
 * Theme key: 'contentful/card'
 *
 * Variants correspond to the `appearance` Contentful field:
 *   - textAboveSwatch  (Figma 3138-3892 / 3138-3895 desktop, 3138-4607 mobile): image → text → swatches
 *   - textBelowSwatch  (Figma 3138-5390): image → swatches → text
 *   - tile             (Figma 3138-5546): "Teaser Tile" — image → text + CTA (no swatch positional constraint)
 */
export const cardStyles = helpers.defineMultiStyleConfig({
  baseStyle: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      position: 'relative',
    },
    imageWrapper: {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
      aspectRatio: '424 / 535',
      sx: {
        '& .swiper': {
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        },
        '& .swiper-wrapper, & .swiper-slide': {
          height: '100%',
        },
        // Hover effect only when lifestyle image is present (data-has-lifestyle="true").
        '&[data-has-lifestyle]': {
          cursor: 'pointer',
        },
        '&[data-has-lifestyle] > .contentful-card__image--primary': {
          transition: 'opacity 0.3s ease',
        },
        '&[data-has-lifestyle] > .contentful-card__image--lifestyle': {
          position: 'absolute',
          inset: 0,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        },
        '@media (hover: hover) and (pointer: fine)': {
          '&[data-has-lifestyle]:hover > .contentful-card__image--primary': {
            opacity: 0,
          },
          '&[data-has-lifestyle]:hover > .contentful-card__image--lifestyle': {
            opacity: 1,
          },
        },
      },
    },
    lifestyleBlock: {
      backgroundColor: 'brandColors.secondaryBGTranslucent15',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      display: 'block',
    },
    swiperImage: {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    lifestyleImage: {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    badge: {
      ...CARD_BADGE_BASE,
      bg: 'brandColors.greyscale90',
      color: 'white',
    },
    swatchSection: {
      width: '100%',
      paddingX: 2,
      paddingY: 3,
    },
    swatchRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: SWATCH_ROW_GAP,
    },
    swatchItem: {
      display: 'inline-block',
      flexShrink: 0,
      width: SWATCH_SIZE,
      height: SWATCH_SIZE,
      borderRadius: SWATCH_RADIUS,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    swatchMore: {
      fontFamily: 'body',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      color: 'primary.darkLinen',
      marginLeft: SWATCH_MORE_OFFSET,
      whiteSpace: 'nowrap',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: '100%',
      sx: CARD_CONTENT_LINE_CLAMP_SX,
    },
    description: {
      fontFamily: 'body',
      fontSize: 'sm',
      fontWeight: 400,
      lineHeight: 1.2,
      color: 'greyscale.70',
      noOfLines: 2,
    },
    skeletonImage: {
      aspectRatio: '424 / 535',
      width: '100%',
    },
    skeletonText: {
      noOfLines: 2,
      spacing: 2,
      mt: 2,
    },
    categoryLink: {
      color: 'greyscale90Alpha.90',
      fontFamily: 'body',
      fontSize: {base: '16px', lg: '20px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: 1.2,
      noOfLines: 3,
      textDecoration: 'none',
      _hover: {
        textDecoration: 'underline',
      },
      sx: {
        '& a, & a *': {
          color: 'brandColors.greyscale90',
          fontFamily: 'body',
          fontSize: {base: '16px', lg: '20px'},
          fontStyle: 'normal',
          fontVariantNumeric: 'lining-nums proportional-nums',
          fontWeight: 500,
          lineHeight: 1.2,
          textDecoration: 'none',
        },
        ...CARD_CONTENT_LINE_CLAMP_SX,
      },
    },
    ctaButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '44px',
      paddingX: 12,
      border: '1px solid',
      borderColor: 'greyscale.100',
      borderRadius: 'sm',
      fontSize: 'sm',
      fontWeight: 'semibold',
      color: 'greyscale.100',
      textDecoration: 'none',
      _hover: {
        bg: 'greyscale.10',
      },
    },
  },

  variants: {
    textAboveSwatch: {
      root: {
        bg: 'brandColors.secondaryBGTranslucent15',
        position: 'relative',
      },
      badge: {
        left: {base: 2, lg: 8},
        top: {base: 2, lg: 8},
      },
      imageWrapper: {
        aspectRatio: {base: 'unset', lg: '424 / 535'},
        height: {base: '250px', lg: 'auto'},
        zIndex: 0,
        margin: '0 auto',
      },
      infoStack: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        left: 0,
        paddingBottom: {base: 6, lg: '64px'},
        paddingTop: {base: 8, lg: 0},
        paddingX: 4,
        position: {base: 'relative', lg: 'absolute'},
        bottom: {lg: 0},
        right: 0,
        width: '100%',
        zIndex: 2,
      },
      content: {
        alignItems: 'center',
        gap: 2,
        padding: 0,
        sx: CARD_CONTENT_LINE_CLAMP_SX,
        textAlign: 'center',
        width: '100%',
      },
      categoryLink: {
        color: 'brandColors.greyscale90',
        fontFamily: 'body',
        fontSize: {base: '16px', lg: '20px'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: 1.2,
        textDecoration: 'none',
        _hover: {
          textDecoration: 'none',
        },
      },
      description: {
        fontSize: {base: '12px', lg: '14px'},
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.2,
        color: 'greyscale.70',
        maxW: '100%',
        width: '100%',
        sx: {
          '& p': {
            margin: 0,
            maxW: '100%',
            width: '100%',
            ...CARD_DESC_LINE_CLAMP,
          },
        },
      },
      swatchSection: {
        display: 'flex',
        justifyContent: 'center',
        padding: 0,
        width: 'auto',
      },
      swatchRow: {
        justifyContent: 'center',
      },
    },

    /**
     * "Text below swatch" — Figma Product range tile v2 (3138-3957 desktop / 3138-4909 mobile).
     * Image (424×535 desktop, 250px tall mobile) → swatches overlay → title + desc.
     */
    textBelowSwatch: {
      imageWrapper: {
        aspectRatio: '424 / 535',
        bg: 'brandColors.secondaryBGTranslucent15',
        minHeight: {'base': '233px', 'lg': '250px'},
        zIndex: 0,
      },
      hoverWrapper: {
        cursor: 'pointer',
        position: 'relative',
        width: '100%',
        //bg: 'brandColors.secondaryBGTranslucent15',
        sx: {
          '& .contentful-card__primary-block': {
            transition: 'opacity 0.3s ease',
          },
          '& .contentful-card__lifestyle-block': {
            position: 'absolute',
            inset: 0,
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          '@media (hover: hover) and (pointer: fine)': {
            '&:hover .contentful-card__primary-block': {
              opacity: 0,
            },
            '&:hover .contentful-card__lifestyle-block': {
              opacity: 1,
            },
          },
        },
      },
      content: {
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: {base: '10px', lg: 2},
        paddingTop: 4,
        paddingX: 4,
        paddingBottom: {base: 4, lg: 0},
        sx: {
          '& a, & a *': {
            color: 'brandColors.greyscale90',
            fontFamily: 'body',
            fontSize: {base: '16px', lg: '20px'},
            fontStyle: 'normal',
            fontVariantNumeric: 'lining-nums proportional-nums',
            fontWeight: 500,
            lineHeight: 1.2,
            textDecoration: 'none',
          },
          ...CARD_CONTENT_LINE_CLAMP_SX,
        },
      },
      categoryLink: {
        color: 'brandColors.greyscale90',
        fontFamily: 'body',
        fontSize: {base: '16px', lg: '20px'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: 1.2,

      },
      description: {
        fontSize: {base: '12px', lg: '14px'},
        fontWeight: 400,
        color: 'greyscale.70',
        noOfLines: 2,
        sx: {
          '& p': {
            margin: 0,
            maxW: '100%',
            width: '100%',
            ...CARD_DESC_LINE_CLAMP,
          },
        },
      },
      swatchSection: {
        position: 'absolute',
        bottom: {base: '30px', lg: '66px'},
        left: '50%',
        transform: 'translateX(-50%)',
        paddingX: 0,
        paddingY: 0,
        width: 'auto',
        zIndex: 1,
      },
      swatchRow: {
        justifyContent: 'center',
      },
    },

    /**
     * "Teaser Tile"
     */
    tile: {
      root: {
        gap: 5,
      },
      imageWrapper: {
        aspectRatio: '424 / 535',
        minHeight: {'base': '233px', 'lg': '250px'},
        zIndex: 0,
        sx: {
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.2))',
            pointerEvents: 'none',
            zIndex: 1,
          },
        },
      },
      content: {
        alignItems: 'flex-start',
        gap: 5,
        maxW: '100%',
        paddingX: 4,
        paddingTop: 0,
        width: '100%',
        paddingBottom: {base: '15px', lg: 0},
        sx: {
          '.chakra-link': {
            width: {base: '100%', lg: 'auto'},
            paddingX: {'base': 'unset', 'lg': '48px'},
            paddingY: '14px',
            minW: {'base': '150px', 'lg': 'unset'},
          },
          '.chakra-link, .chakra-link .chakra-text': {
            fontSize: {base: '13px', lg: '14px'},
            fontWeight: 600,
            lineHeight: 1.2,
            textAlign: 'center',
            '&:hover': {
              textDecoration: 'none',
            },
          },
          '&&.contentful-card__content--tile .chakra-link, &&.contentful-card__content--tile .chakra-link .chakra-text': {
            color: {base: 'rgba(27, 26, 24, 0.90)', lg: 'black'},
            '&:hover,&:focus': {
              textDecoration: 'none',
              color: 'white',
              '& p': {
                color: 'white',
              },
            },
          },
        },
      },
      description: {
        fontFamily: 'body',
        fontSize: {base: '14px', lg: '20px'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: {base: 1.2, lg: 1.5},
        color: 'brandColors.greyscale90',
        maxW: '100%',
        width: '100%',
        sx: {
          '& p': {
            margin: 0,
            maxW: '100%',
            width: '100%',
          },
        },
      },
    },
  },

  defaultProps: {
    variant: 'textBelowSwatch',
  },
});
